// File: packages/components/src/homepage/proof/ProofDemosSection.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button, Badge, VisualElement } from '@dopomogai/ui';
import { Play } from 'lucide-react';

type Accent =
  | 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose'
  | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia'
  | 'pink' | 'slate' | 'gray';

const stripeClass = (accent?: Accent) => {
  switch (accent) {
    case 'violet': return 'bg-violet-400';
    case 'blue': return 'bg-blue-400';
    case 'cyan': return 'bg-cyan-400';
    case 'amber': return 'bg-amber-400';
    case 'rose': return 'bg-rose-400';
    case 'indigo': return 'bg-indigo-400';
    case 'purple': return 'bg-purple-400';
    case 'teal': return 'bg-teal-400';
    case 'orange': return 'bg-orange-400';
    case 'lime': return 'bg-lime-400';
    case 'fuchsia': return 'bg-fuchsia-400';
    case 'pink': return 'bg-pink-400';
    case 'slate': return 'bg-slate-400';
    case 'gray': return 'bg-gray-400';
    default: return 'bg-emerald-400';
  }
};

export type ProofDemoItem = {
  title?: string;
  titleKey?: string;
  text?: string;
  textKey?: string;

  visual?: any;

  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaAlt?: string;
  posterUrl?: string;

  // New metadata
  kind?: 'demo' | 'projection' | 'case-study';
  duration?: string;       // e.g., '3:21'
  accent?: Accent;

  cta?: { label?: string; labelKey?: string; actionId: string };
};

export type ProofDemosSectionProps = {
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  items: ProofDemoItem[];
  columns?: 2 | 3;
  onAction?: (actionId: string, payload?: any) => void;
  className?: string;
  defaultAccent?: Accent;     // section-wide default for stripes
  equalizeHeights?: boolean;  // consistent card heights
};

export const ProofDemosSection: React.FC<ProofDemosSectionProps> = ({
  title,
  titleKey,
  subtitle,
  subtitleKey,
  items,
  columns = 3,
  onAction,
  className,
  defaultAccent = 'slate',
  equalizeHeights = true,
}) => {
  const { t } = useTranslation('homepage');
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  const gridCols = columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2';

  return (
    <section className={clsx('bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {(resolvedTitle || resolvedSubtitle) && (
          <div className="mb-8 md:mb-10">
            {resolvedTitle ? (
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">{resolvedTitle}</h2>
            ) : null}
            {resolvedSubtitle ? (
              <p className="mt-3 text-slate-700 text-base md:text-lg max-w-3xl">{resolvedSubtitle}</p>
            ) : null}
          </div>
        )}

        <div className={clsx('grid gap-5 md:gap-6', gridCols)}>
          {items.map((it, idx) => {
            const cardTitle = it.titleKey ? t(it.titleKey) : (it.title ?? '');
            const cardText = it.textKey ? t(it.textKey) : (it.text ?? '');
            const accent = it.accent ?? defaultAccent;

            const mediaNode = it.visual ? (
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl border-2 border-black bg-slate-100">
                <VisualElement visual={it.visual} isThumbnail={false} />
              </div>
            ) : it.mediaUrl ? (
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl border-2 border-black bg-slate-100">
                {it.mediaType === 'video' ? (
                  <>
                    <video className="h-full w-full object-cover" controls preload="metadata" poster={it.posterUrl}>
                      <source src={it.mediaUrl} />
                    </video>
                    {/* Play overlay hint on hover (decorative; controls still show) */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="flex items-center justify-center w-14 h-14 rounded-full border-2 border-black bg-white/90">
                        <Play size={22} className="text-slate-900" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={it.mediaUrl}
                    alt={it.mediaAlt || cardTitle || 'demo'}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* Badges */}
                <div className="absolute left-2 top-2 flex gap-1">
                  {it.kind ? (
                    <span className="inline-flex items-center rounded-md border-2 border-black bg-white/90 px-2 py-0.5 text-xs font-bold text-slate-900">
                      {it.kind === 'demo' ? 'Demo' : it.kind === 'projection' ? 'Projection' : 'Case study'}
                    </span>
                  ) : null}
                  {it.duration ? (
                    <span className="inline-flex items-center rounded-md border-2 border-black bg-white/90 px-2 py-0.5 text-xs font-bold text-slate-900">
                      {it.duration}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null;

            return (
              <article
                key={idx}
                className={clsx(
                  'group relative border-2 border-black rounded-2xl bg-white p-4 md:p-5',
                  'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
                  equalizeHeights && 'flex flex-col'
                )}
              >
                {/* Top stripe */}
                <div aria-hidden="true" className={clsx('absolute inset-x-2 top-2 h-2 rounded-full', stripeClass(accent))} />

                {mediaNode}

                <div className="mt-4 flex-1">
                  <h3 className="text-lg md:text-xl font-extrabold text-slate-900">{cardTitle}</h3>
                  {cardText ? <p className="mt-2 text-slate-700">{cardText}</p> : null}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200">
                  {it.cta ? (
                    <Button
                      variant="secondary"
                      text={it.cta.labelKey ? t(it.cta.labelKey) : (it.cta.label ?? 'Learn more')}
                      onClick={() => onAction?.(it.cta!.actionId, { index: idx })}
                      fullWidth
                    />
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProofDemosSection;
